import EcoScore20 from '@assets/images/eco_score_20.svg'
import EcoScore40 from '@assets/images/eco_score_40.svg'
import EcoScore60 from '@assets/images/eco_score_60.svg'
import EcoScore80 from '@assets/images/eco_score_80.svg'
import EcoScore100 from '@assets/images/eco_score_100.svg'
import canvas from '@classes/canvas'
import { MeterId } from '@constants/gauges_meters_consts'
import { getPropertyValue } from '@constants/utils'

import { divideIfNotZero } from '../helper/canvasDraw'
import type { MainParams, StyleParams } from '../Meter'
import type { GaugeDrawResult } from './gauge_types'

const ECO_URLS = [EcoScore20, EcoScore40, EcoScore60, EcoScore80, EcoScore100]
const cache: Record<string, HTMLImageElement> = {}

function getEcoImg (value: number): HTMLImageElement {
  const idx = Math.min(4, Math.max(0, Math.floor(value / 20)))
  const src = ECO_URLS[idx]
  if (!cache[src]) {
    const img = new Image()
    img.src = src
    cache[src] = img
  }
  return cache[src]
}

function drawContainInCircle (
  mainParams: MainParams,
  styleParams: StyleParams,
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  newCurrentValue: number,
  paddingRatio = 0.65,
  verticalShift = -30
) {
  const { dpx, center, radius, formatter, unit } = mainParams
  const { color } = styleParams
  if (!img.complete || img.naturalWidth === 0) {
    return
  }

  const innerDiameter = radius * 2 * paddingRatio
  const scale = Math.min(innerDiameter / img.naturalWidth, innerDiameter / img.naturalHeight)

  const drawW = img.naturalWidth * scale
  const drawH = img.naturalHeight * scale
  const x = center - drawW / 2
  const y = center - drawH / 2 + verticalShift

  ctx.drawImage(img, x, y, drawW, drawH)

  const formatted = `${formatter(newCurrentValue)} ${unit}`
  ctx.font = `${12 * dpx}px ${getPropertyValue('--font-family')}`
  ctx.fillStyle = color ?? ''

  const textWidth = ctx.measureText(formatted).width
  const textX = center - textWidth / 2
  const textY = center + radius * 0.8

  ctx.fillText(formatted, textX, textY)
}

export function drawEcoScoreGauge (main: MainParams, style: StyleParams): GaugeDrawResult {
  const { prevValue, maxValue, value, ctx, redraw, dpx, center, radius, currentValue } = main
  const { arcColor } = style

  let newCurrentValue: number = currentValue
  const from = divideIfNotZero(Number(prevValue), Number(maxValue)) * 100
  const to = divideIfNotZero(100, Number(maxValue)) * Number(value)

  const img = getEcoImg(Number(value))

  const drawing = (current: number) => {
    newCurrentValue = Number(Number(maxValue) * (current / 100))

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    redraw()
    canvas.drawArc(ctx, center, center, radius, 0, Math.PI * 2, arcColor, dpx + 1)

    if (img.complete) {
      drawContainInCircle(main, style, ctx, img, newCurrentValue)
    } else {
      img.onload = () => {
        drawContainInCircle(main, style, ctx, img, newCurrentValue)
      }
    }
  }

  const animateId = canvas.animate(from, to, drawing, 2, MeterId.ECO_SCORE)
  return { animateId, newCurrentValue }
}
